import { useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X, Camera } from "lucide-react";
import { toast } from "sonner";

/**
 * QRScannerModal — opens the camera and calls onDecode(text) as soon as it
 * decodes a QR code. Uses html5-qrcode which supports iOS Safari + Android
 * Chrome and reuses the front-end camera stream cleanly.
 */
export default function QRScannerModal({ open, onClose, onDecode }) {
  const containerId = "sally-qr-reader";
  const qrRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const start = async () => {
      try {
        const scanner = new Html5Qrcode(containerId, { verbose: false });
        qrRef.current = scanner;
        // Prefer back camera on mobile
        await scanner.start(
          { facingMode: { ideal: "environment" } },
          { fps: 10, qrbox: { width: 240, height: 240 }, aspectRatio: 1.0 },
          (decodedText) => {
            if (cancelled) return;
            cancelled = true;
            onDecode?.(decodedText);
          },
          () => {} // ignore per-frame errors
        );
      } catch (e) {
        toast.error("No se pudo acceder a la cámara. Revisa los permisos.");
        onClose?.();
      }
    };
    start();
    return () => {
      cancelled = true;
      if (qrRef.current) {
        try { qrRef.current.stop().then(() => qrRef.current.clear()).catch(() => {}); } catch {}
        qrRef.current = null;
      }
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950/90 flex items-center justify-center p-4" data-testid="qr-scanner-modal">
      <div className="bg-white rounded-sm max-w-md w-full overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-900 text-white">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5" />
            <div className="font-black tracking-tight" style={{ fontFamily: "Manrope" }}>Escanear Ticket</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded-sm" data-testid="qr-scanner-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">
          <div id={containerId} className="w-full aspect-square bg-black overflow-hidden rounded-sm" />
          <div className="text-xs text-slate-500 mt-3 text-center">
            Apunta la cámara al código QR del ticket
          </div>
        </div>
      </div>
    </div>
  );
}
